import { TUser, useGetUsersQuery } from "@/shared/services";
import { useAnalyticsStoreShallow } from "@pages/analytics/store";
import { useCallback, useMemo } from "react";

export const UsersGroup = ({
  searchTerm,
  onClose,
}: {
  searchTerm: string;
  onClose: () => void;
}) => {
  const { setMultipleFilters } = useAnalyticsStoreShallow((s) => ({
    setMultipleFilters: s.setMultipleFilters,
  }));
  const { data: users, isLoading } = useGetUsersQuery();
  const filteredUsers = useMemo(() => {
    return users?.filter((user) =>
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [users, searchTerm]);
  const handleUserChange = useCallback(
    (user: TUser) => {
      setMultipleFilters({
        user: { id: user.oktaId, name: user.firstName },
        location: "",
        story: { id: "", title: "" },
        version: { id: "", title: "" },
        folder: { id: "", name: "" },
      });
      onClose();
    },
    [setMultipleFilters, onClose],
  );
  return (
    <div className="tw-p-1">
      <span className="tw-py-1 tw-px-2 tw-block tw-text-blue-500 tw-text-xs tw-font-sans">
        Users
      </span>
      {isLoading ? (
        <UsersSkeleton />
      ) : filteredUsers?.length === 0 ? (
        <div className="tw-py-2 tw-text-center tw-text-sm tw-text-blue-500">
          {searchTerm ? "No users found" : "No users available"}
        </div>
      ) : (
        filteredUsers?.map((user) => (
          <UserItem
            key={user.oktaId}
            user={user}
            onUserChange={handleUserChange}
          />
        ))
      )}
    </div>
  );
};

const UserItem = ({
  user,
  onUserChange,
}: {
  user: TUser;
  onUserChange: (user: TUser) => void;
}) => {
  const initials = user.firstName
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("");
  return (
    <button
      type="button"
      className="tw-py-1.5 tw-w-full tw-px-2 tw-outline-none tw-text-blue tw-text-sm tw-transition-colors hover:tw-bg-blue-50 tw-cursor-pointer tw-flex tw-gap-2 tw-items-center tw-justify-start tw-rounded"
      onClick={() => onUserChange(user)}
    >
      <div className="tw-flex tw-items-center tw-justify-center tw-size-4 tw-rounded-full tw-overflow-hidden tw-border tw-border-solid tw-border-blue/10 tw-bg-blue-100 tw-font-sans tw-text-blue/50 tw-text-[6px]">
        {user?.profile?.picture ? (
          <img
            src={user.profile.picture}
            alt={user.firstName}
            className="tw-size-full tw-object-cover"
          />
        ) : (
          initials
        )}
      </div>
      {user.firstName}
    </button>
  );
};

const SKELETON_WIDTHS = [65, 80, 45, 90, 55];

const UsersSkeleton = () => {
  return (
    <>
      {SKELETON_WIDTHS.map((width) => (
        <div
          key={width}
          className="tw-flex tw-h-8 tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-animate-skeleton-loading"
        >
          <span
            className="tw-block tw-h-2 tw-w-24 tw-rounded-lg tw-bg-blue-100"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </>
  );
};
